import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, onMounted, watch } from 'vue';
import { PackageService } from '@/Services/PackageService';
import { useToken } from '@/useToken';
import { ITransactionExtension, ITransactionExtensionFieldName } from '@/Interfaces';
import { errToMessage, groupBy } from '@samc/common';
import { format as formatDate } from 'date-fns';
import { Validations } from '@samc/common-ui';

import { Toolbar } from '@samc/centricity-core';
import { TransactionExtensionFieldNameModal } from '@/Components/Modals';

type FieldType = 'rate' | 'text' | 'decimal' | 'date';
interface IClientTransactionExtensionFieldName extends ITransactionExtensionFieldName {
  value: any;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'PackageCustomFieldsTab',
  props: {
    verificationToken: { type: String, required: true },
    packageId: { type: String, required: true },
    isDeal: { type: Boolean, required: false }
  },
  setup(__props: any) {

const props = __props as {
  verificationToken: string,
  packageId: string,
  isDeal?: boolean
};

const dateFormat: string = 'MM/dd/yyyy';
const fieldMap: Record<string, FieldType> = {
  customrate: 'rate',
  customtext: 'text',
  customdecimal: 'decimal',
  customdate: 'date'
} as const;



const { token } = useToken();

const model = ref<ITransactionExtension>({ packageId: props.packageId });
const fields = ref<IClientTransactionExtensionFieldName[]>([]);
const selectedField = ref<IClientTransactionExtensionFieldName | null>(null);
const validations = ref<Validations<ITransactionExtension>>({});
const showValidations = ref(false);

const service = computed(() => new PackageService(props.verificationToken, null, token.value));
const fieldGroups = computed(() => groupBy(fields.value, field => fieldMap[field.fieldName.replace(/\d+$/, '').toLowerCase()]));
const hasValidationErrors = computed(() => Object.keys(validations.value).length > 0);

const getDisplayName = (fieldName: keyof ITransactionExtension, defaultName: string): string => {
  const field = fields.value.find(f => f.fieldName === fieldName);
  return field?.displayName ?? defaultName;
};

/**
 * Updates the custom fields for the package
 */
const updateModel = async () => {
  try {
    showValidations.value = true;
    if (hasValidationErrors.value) {
      window.showAlert?.warning('Please fix the validation errors before saving.', 'See below for details.');
      return;
    }

    window.loading?.('Updating Custom Fields...');
    model.value = await service.value.updateTransactionExtension(props.packageId, model.value);

    window.showAlert?.success('Custom fields updated successfully.');
  } catch (err) {
    window.showAlert?.({
      type: 'error',
      title: 'Failed to updated custom fields',
      description: 'For more detailed information, please contact support team.',
      errors: errToMessage(err),
      closingOption: 'holdOpen'
    });
  } finally {
    window.loading?.();
  }
};

const getTransactionExtensionFieldNames = async () => {
  try {
    return await service.value.getTransactionExtensionFieldNamesAsync(props.isDeal ?? false);
  } catch (err) {
    window.showAlert?.({
      type: 'error',
      title: 'Failed to load transaction extension field names',
      description: 'For more detailed information, please contact support team.',
      errors: errToMessage(err),
      closingOption: 'holdOpen'
    });
  }
};

/**
 * Helper method to update the transaction extension field name
 * 
 * @param model The model to update the transaction extension field name
 */
const updateTransactionExtensionFieldNames = async (model: ITransactionExtensionFieldName) => {
  try {
    window.loading?.('Updating Transaction Extension Field Name...');
    await service.value.updateTransactionExtensionFieldNameAsync(model);
    selectedField.value = null;

    const index = fields.value.findIndex(f => f.fieldName === model.fieldName);
    if (index !== -1) {
      fields.value[index].displayName = model.displayName;
    }
  } catch (err) {
    window.showAlert?.({
      type: 'error',
      title: 'Failed to update transaction extension field name',
      description: 'For more detailed information, please contact support team.',
      errors: errToMessage(err),
      closingOption: 'holdOpen'
    });
  } finally {
    window.loading?.();
  }
};

/**
 * Converts the field name to what is coming back from the server
 * 
 * @param fieldName The field name to convert to a model key
 */
const getModelKey = (fieldName: string) => {
  return fieldName.charAt(0).toLowerCase() + fieldName.slice(1) as keyof ITransactionExtension;
};

/**
 * Converts the date/string to a formatted date string
 * 
 * @param date The date to convert to a string
 */
const convertDate = (date?: Date | string | null): string => {
  if (date === undefined || date === null || date === '') return '';
  if (typeof date === 'string') return formatDate(new Date(date), dateFormat);
  return formatDate(date, dateFormat);
};

/**
 * Helper method meant to setup the value getter and setter for the field to bound to the model
 * 
 * @param field The field to convert to a client-side model
 */
const asClient = (field: ITransactionExtensionFieldName): IClientTransactionExtensionFieldName => {
  return {
    ...field,
    get value() {
      const key = getModelKey(field.fieldName);
      return model.value[key];
    },
    set value(value: any) {
      const key = getModelKey(field.fieldName);
      (model.value[key] as any) = value;
    }
  };
};

const getValidations = (value: ITransactionExtension) => {
  const result: Validations<ITransactionExtension> = { };
  const rateMax = 9999.999999;
  const rateMin = -9999.999999;
  if ((value.customRate1 ?? 0) > rateMax || (value.customRate1 ?? 0) < rateMin)
    result.customRate1 = `${getDisplayName('customRate1', 'Rate 1')} must be between ${rateMin} and ${rateMax}.`;
  if ((value.customRate2 ?? 0) > rateMax || (value.customRate2 ?? 0) < rateMin)
    result.customRate2 = `${getDisplayName('customRate2', 'Rate 2')} must be between ${rateMin} and ${rateMax}.`;
  if ((value.customRate3 ?? 0) > rateMax || (value.customRate3 ?? 0) < rateMin)
    result.customRate3 = `${getDisplayName('customRate3', 'Rate 3')} must be between ${rateMin} and ${rateMax}.`;

  const decimalMax = 999999999999.99;
  const decimalMin = -999999999999.99;
  if ((value.customDecimal1 ?? 0) > decimalMax || (value.customDecimal1 ?? 0) < decimalMin)
    result.customDecimal1 = `${getDisplayName('customDecimal1', 'Decimal 1')} must be between ${decimalMin} and ${decimalMax}.`;
  if ((value.customDecimal2 ?? 0) > decimalMax || (value.customDecimal2 ?? 0) < decimalMin)
    result.customDecimal2 = `${getDisplayName('customDecimal2', 'Decimal 2')} must be between ${decimalMin} and ${decimalMax}.`;
  if ((value.customDecimal3 ?? 0) > decimalMax || (value.customDecimal3 ?? 0) < decimalMin)
    result.customDecimal3 = `${getDisplayName('customDecimal3', 'Decimal 3')} must be between ${decimalMin} and ${decimalMax}.`;

  if ((value.customText1?.length ?? 0) > 255)
    result.customText1 = `${getDisplayName('customText1', 'Text 1')} must be less than or equal to 255 characters.`;
  if ((value.customText2?.length ?? 0) > 255)
    result.customText2 = `${getDisplayName('customText2', 'Text 2')} must be less than or equal to 255 characters.`;
  if ((value.customText3?.length ?? 0) > 255)
    result.customText3 = `${getDisplayName('customText3', 'Text 3')} must be less than or equal to 255 characters.`;

  return result;
};

watch(model, (newValue) => {
  validations.value = getValidations(newValue);
}, { deep: true });

onMounted(async () => {
  try {
    window.loading?.('Loading Custom Fields...');
    model.value = await service.value.getTransactionExtension(props.packageId);

    fields.value = ((await getTransactionExtensionFieldNames()) ?? []).map(asClient);
  } catch (err) {
    window.showAlert?.({
      type: 'error',
      title: 'Failed to load transaction extension',
      description: 'For more detailed information, please contact support team.',
      errors: errToMessage(err),
      closingOption: 'holdOpen'
    });
  } finally {
    window.loading?.();
  }
});

return { __sfc: true,dateFormat, fieldMap, props, token, model, fields, selectedField, validations, showValidations, service, fieldGroups, hasValidationErrors, getDisplayName, updateModel, getTransactionExtensionFieldNames, updateTransactionExtensionFieldNames, getModelKey, convertDate, asClient, getValidations, Toolbar, TransactionExtensionFieldNameModal }
}

})